import React from "react";
import { Card, Typography, Tooltip, Checkbox, Box } from "@mui/material";
import { CheckBoxOutlineBlank, CheckBox } from "@mui/icons-material";
import { sourcesPanelStyles } from "../PanelStyles";
import type {
  MateriomFormulationSource,
  ScientificSource,
  Source,
} from "../../types/frontend";
import { SourceTypeValues } from "../../types/frontend";
import ScientificSourceCardContent from "./ScientificSourceCardContent";
import MateriomFormulationCardContent from "./MateriomFormulationCardContent";

/**
 * Utility function to transform ContentType enum values into display labels.
 * Converts strings like "SCIENTIFIC_SOURCE" to "Scientific Source".
 * @param {string} type - The type string to transform.
 * @returns {string} - The transformed display label.
 */
const getSourceTypeLabel = (type: string): string => {
  return type
    .replace(/_/g, " ")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ")
    .replace(/s$/, ""); // Remove trailing 's'
};

/**
 * Props for the SourceCard component.
 */
interface SourceCardProps {
  source: Source; // The source data to display
  isAttached: boolean; // Indicates if the source is attached to the chat
  onClick: () => void; // Function to handle card click
  onCheckboxClick: (e: React.MouseEvent) => void; // Function to handle checkbox click
  expandedCardId: string | null; // ID of the currently expanded card
  isPanelExpanded: boolean; // Indicates if the panel is expanded
  index: number; // Index of the card in the list
}

/**
 * SourceCard Component
 *
 * Renders a card displaying information about a source, with options to expand
 * and attach the source to a chat.
 *
 * @param {SourceCardProps} props - Component props
 * @returns {JSX.Element} Rendered SourceCard component
 */
const SourceCard: React.FC<SourceCardProps> = ({
  source,
  isAttached,
  onClick,
  onCheckboxClick,
  expandedCardId,
  isPanelExpanded,
  index,
}) => {
  return (
    <Card
      key={source.id || `${source.id}-${index}`}
      sx={{
        ...sourcesPanelStyles.card,
        mx: 2,
        my: 1,
        transition: "all 0.3s ease",
        height: expandedCardId === source.id ? "auto" : "fit-content",
        minHeight: expandedCardId === source.id ? "400px" : "unset",
        bgcolor: expandedCardId === source.id ? "#F5F5F5" : "eff6f4",
      }}
      onClick={onClick}
      data-attr="source-card"
    >
      {/* Top bar with category pill and add to chat button */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 1,
          py: 0.5,
        }}
      >
        {/* Category pill */}
        <Typography
          component="span"
          sx={{
            ...sourcesPanelStyles.categoryPill,
            bgcolor: "#aeccc7",
            color: "#5c948e",
          }}
        >
          {getSourceTypeLabel(source.type)}
        </Typography>
        {/* Add to chat button */}
        <Tooltip
          title={isAttached ? <>Remove from chat</> : <>Add to chat</>}
          placement="bottom"
          slotProps={{
            tooltip: {
              sx: {
                textAlign: "center",
              },
            },
            popper: {
              modifiers: [
                {
                  name: "offset",
                  options: {
                    offset: [0, -4],
                  },
                },
              ],
            },
          }}
        >
          <span>
            <Checkbox
              disableRipple
              icon={<CheckBoxOutlineBlank fontSize="medium" />}
              checkedIcon={
                <CheckBox fontSize="medium" sx={{ color: "primary.main" }} />
              }
              checked={isAttached}
              onClick={onCheckboxClick}
              size="medium"
              sx={sourcesPanelStyles.favIcon}
              inputProps={{
                "aria-label": isAttached ? "Remove from chat" : "Add to chat",
              }}
            />
          </span>
        </Tooltip>
      </Box>
      {/* Render content based on source type */}
      {source.type === SourceTypeValues.ScientificSource && (
        <ScientificSourceCardContent
          source={source as ScientificSource}
          isExpanded={expandedCardId === source.id}
        />
      )}
      {source.type === SourceTypeValues.MateriomFormulation && (
        <MateriomFormulationCardContent
          source={source as MateriomFormulationSource}
        />
      )}
    </Card>
  );
};

export default SourceCard;
